import DayJS from "@components/DayJS";
import { useDialog } from "@hooks/useDialog";
import permissionManager from "@lib/permission";
import { Announcement as AnnouncementIcon } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from "@mui/material";
import trpcClient from "@services/rpc-client";
import { displayMessage } from "@src/utils";
import Markdown from "markdown-to-jsx";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import useNotificationMenuStyles from "./NotificationMenu.styles";
import { CacheKeyTypes } from "@services/cache";

type ViewProps = {
  menuOpen: boolean;
  handleClose: () => void;
};

export const Announcements = ({ menuOpen, handleClose }: ViewProps) => {
  const { data, error, refetch } = useQuery({
    queryKey: CacheKeyTypes.Announcements,
    queryFn: () => trpcClient.query("announcements"),
    enabled: menuOpen,
  });

  const isAdmin = useMemo(() => permissionManager.userCanAccess("all"), []);

  const [viewing, setViewing] = useState<null | (typeof announcements)[0]>(null);

  const { DialogCloseButton, onClose, open, setOpen } = useDialog(false, {
    onClose: () => setViewing(null),
  });

  const onAnnouncementClicked = (ann: (typeof announcements)[0]) => {
    setViewing(ann);
    setOpen(true);
  };

  const classes = useNotificationMenuStyles();

  if (error) {
    return (
      <Box textAlign={"center"} color="red">
        {`Error: ${(error as Error)?.message}`}
      </Box>
    );
  }

  if (!data) {
    return <Typography textAlign={"center"}>{"Loading..."}</Typography>;
  }

  const { announcements } = data;

  return (
    <>
      {announcements.length === 0 && (
        <Typography align="center" style={{ color: "#eee" }}>
          {"None right now"}
        </Typography>
      )}

      {announcements.map((ann) => (
        <Box key={ann.id} className={classes.menuItem} onClick={() => onAnnouncementClicked(ann)}>
          <div className={classes.notificationItemHeaderContainer}>
            {ann.type && (
              <>
                <AnnouncementIcon style={{ fontSize: 18 }} />
                <span>&nbsp;{ann.type}</span>
              </>
            )}
          </div>
          <Typography sx={{ whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}>{ann.title}</Typography>

          <div className={classes.notifAge}>
            <DayJS date={ann.created_at} fromNow />
          </div>
        </Box>
      ))}

      {viewing && (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
          <DialogCloseButton />
          <DialogTitle>{viewing.title}</DialogTitle>
          <DialogContent dividers>
            <Markdown
              options={{
                overrides: {
                  a: { component: Link, props: { target: "_blank" } },
                  ul: { props: { style: { padding: 0, listStylePosition: "inside" } } },
                },
              }}
            >
              {viewing.content_md}
            </Markdown>
          </DialogContent>

          {isAdmin && (
            <DialogActions>
              <Button
                color="warning"
                onClick={async () => {
                  const r = await trpcClient.mutation("announcement/delete", { id: viewing.id });
                  displayMessage(r.message, "success");
                  setViewing(null);
                  refetch();
                }}
              >
                Delete
              </Button>
            </DialogActions>
          )}
        </Dialog>
      )}
    </>
  );
};

export default Announcements;
